import { useState, useEffect } from "react";
import { Box, Typography, Grid, useMediaQuery, useTheme, Snackbar, Alert, Skeleton, ToggleButtonGroup, ToggleButton, Chip } from "@mui/material";
import Pagination from "./components/Pagination";
import { FirstRowElementsComponent } from "../ClientList/components/ClientListParts";
import AgentCard from "./AgentCard";
import AgentSlidingPanel from "./components/AgentSlidingPanel";
import useFetchAgentsDetails from "./hooks/useFetchAllAgentDetails";
import useUpdateAgentPersonalDetails from "./hooks/useUpdateAgentPersonalDetails";
import useUpdateAgentAvatar from "./hooks/useUpdateAgentAvatar";
import PasswordSetupAPI from "../../api/PasswordSetupAPI";

const AgentCardSkeleton = () => (
	<Box sx={{ p: 2, height: "100%" }}>
		<Box display="flex" alignItems="center" mb={2}>
			<Skeleton variant="circular" width={60} height={60} />
			<Box ml={2} flex={1}>
				<Skeleton variant="text" width="60%" height={24} />
				<Skeleton variant="text" width="40%" height={20} />
			</Box>
			<Skeleton variant="circular" width={24} height={24} />
		</Box>
		<Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
		<Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
		<Box display="flex" gap={1} mb={2}>
			<Skeleton variant="rounded" width={80} height={24} />
		</Box>
		<Box display="flex" justifyContent="space-between" mb={1}>
			<Skeleton variant="text" width="40%" height={20} />
			<Skeleton variant="text" width="30%" height={20} />
		</Box>
		<Box display="flex" justifyContent="space-between">
			<Skeleton variant="text" width="40%" height={20} />
			<Skeleton variant="text" width="50%" height={20} />
		</Box>
	</Box>
);

const AgentManagement = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [passwordSetupLoading, setPasswordSetupLoading] = useState(false);
	const { agents, setAgents, loading } = useFetchAgentsDetails();
	const { updateAgentPersonalDetails, loading: updateProfileLoading } = useUpdateAgentPersonalDetails();
	const { updateAgentAvatar, loading: updateAvatarLoading } = useUpdateAgentAvatar();

	const [openPanel, setOpenPanel] = useState(false);

	const [selectedAgentId, setSelectedAgentId] = useState(null);
	const selectedAgent = agents.find((a) => a.user_id === selectedAgentId) ?? null;

	//search and pagination frontend
	const [searchQuery, setSearchQuery] = useState("");
	const handleSearchChange = (value) => setSearchQuery(value);

	// This page is the only admin surface that lists opted-out agents — everywhere
	// else they are hidden. Opted in is the default view; the other tab is where an
	// admin goes to find someone and opt them back in.
	const [showOptedOut, setShowOptedOut] = useState(false);

	const [pageSize, setPageSize] = useState(9);
	const [currentPage, setCurrentPage] = useState(1); // 1-based to match "Page X of Y"

	const optedOutCount = agents.filter((agent) => agent.suspended === 1).length;

	const filteredAgents = agents.filter((agent) => {
		if ((agent.suspended === 1) !== showOptedOut) return false;

		const query = searchQuery.toLowerCase();
		return agent.display_name.toLowerCase().includes(query) || agent.email?.toLowerCase().includes(query);
	});

	const pageCount = Math.max(1, Math.ceil(filteredAgents.length / pageSize));
	const startIndex = (currentPage - 1) * pageSize;
	const endIndex = startIndex + pageSize;
	const displayedAgents = filteredAgents.slice(startIndex, endIndex);

	// Switching tabs changes the result set, so page 3 of the old one is meaningless.
	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, showOptedOut]);

	//==============================

	const handleOpenSlidingPanel = (agent) => {
		setSelectedAgentId(agent.user_id);
		setOpenPanel(true);
	};

	// Personal details save — API call lives here, panel just triggers it.
	const handleUpdateAgentProfile = async (payload) => {
		const { user_id, first_name, last_name, email, address, phone, dob, agency_name, npn_number, license_number, license_status } = payload;

		const data = { first_name, last_name, email, address, phone, dob, agency_name, npn_number, license_number, license_status };

		const result = await updateAgentPersonalDetails(user_id, data);

		if (!result.success) {
			setSnackbar({ open: true, message: result.error || "Failed to update agent profile", severity: "error" });
			return false;
		}

		setAgents((prev) => prev.map((a) => (a.user_id === user_id ? { ...a, ...result.data.data } : a)));
		setSnackbar({ open: true, message: "Successfully updated agent profile!", severity: "success" });
		return true;
	};

	// Avatar save — separate from personal details, own loading state, own API call.
	const handleUpdateAgentAvatar = async (agentId, file) => {
		const formData = new FormData();
		formData.append("agent_id", agentId);
		formData.append("attachment", file);

		const result = await updateAgentAvatar(formData);

		if (!result.success) {
			setSnackbar({ open: true, message: result.error || "Failed to update agent avatar", severity: "error" });
			return { success: false };
		}

		const newAvatarUrl = result.data?.data?.avatar_url;

		setAgents((prev) => prev.map((a) => (a.user_id === agentId ? { ...a, avatar: newAvatarUrl } : a)));
		setSnackbar({ open: true, message: "Profile photo updated successfully!" });
		return { success: true, avatarUrl: newAvatarUrl };
	};

	const handleUpdateAgentPassword = async () => {
		setPasswordSetupLoading(true);
		try {
			const result = await PasswordSetupAPI.sendLink(selectedAgentId);

			setSnackbar({
				open: true,
				message: result.message || "Password setup link sent successfully!",
				severity: "success",
			});

			return result;
		} catch (error) {
			const message = error?.message || "Something went wrong sending the password setup link. Try again later";

			setSnackbar({
				open: true,
				message,
				severity: "error",
			});

			return {
				success: false,
				message,
			};
		} finally {
			setPasswordSetupLoading(false);
		}
	};

	return (
		<Box sx={{ p: 3 }}>
			{/* Header */}
			<Grid container spacing={2} alignItems="center" mb={3}>
				<Grid size={{ xs: 12, md: "auto" }}>
					<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
						AGENT MANAGEMENT
					</Typography>
				</Grid>

				<Grid size={{ xs: 12, md: "grow" }} display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={1.5} flexWrap="wrap">
					{/* Sibling rather than a prop on FirstRowElementsComponent, which is
					    shared with ClientList and has no business knowing about opt-in. */}
					<ToggleButtonGroup
						exclusive
						size="small"
						value={showOptedOut ? "out" : "in"}
						onChange={(e, next) => {
							if (next !== null) setShowOptedOut(next === "out");
						}}
						sx={{
							"& .MuiToggleButton-root": {
								textTransform: "none",
								fontSize: 13,
								fontWeight: 600,
								px: 2,
								borderColor: "#D5DBE3",
								color: "text.secondary",
								backgroundColor: "transparent",
								"&.Mui-selected": {
									backgroundColor: "#EBF5FF !important",
									color: "#1D54A2",
									"&:hover": { backgroundColor: "#EBF5FF !important" },
								},
							},
						}}
					>
						<ToggleButton value="in">Opted In</ToggleButton>
						<ToggleButton value="out">
							Opted Out
							{optedOutCount > 0 && (
								<Chip
									label={optedOutCount}
									size="small"
									sx={{
										ml: 0.75,
										height: 18,
										fontSize: 11,
										fontWeight: 700,
										bgcolor: showOptedOut ? "#1D54A2" : "#ECEFF1",
										color: showOptedOut ? "#fff" : "text.secondary",
										"& .MuiChip-label": { px: 0.75 },
									}}
								/>
							)}
						</ToggleButton>
					</ToggleButtonGroup>

					<FirstRowElementsComponent searchTerm={searchQuery} onSearchChange={handleSearchChange} placeHolder="Name, Email" />
				</Grid>
			</Grid>

			{/* Agent Grid */}
			<Grid container rowSpacing={3} columnSpacing={1.5}>
				{loading ? (
					Array.from({ length: pageSize }).map((_, index) => (
						<Grid key={`skeleton-${index}`} size={{ xs: 12, sm: 6, md: 4 }}>
							<AgentCardSkeleton />
						</Grid>
					))
				) : displayedAgents.length > 0 ? (
					displayedAgents.map((agent) => (
						<Grid key={agent.user_id} size={{ xs: 12, sm: 6, md: 4 }}>
							<AgentCard agent={agent} handleOpenSlidingPanel={handleOpenSlidingPanel} />
						</Grid>
					))
				) : (
					<Grid size={{ xs: 12 }}>
						<Box display="flex" justifyContent="center" alignItems="center" minHeight={300} flexDirection="column" gap={2}>
							<Typography variant="h6" color="text.secondary">
								{showOptedOut ? "No opted-out agents" : "No agent found"}
							</Typography>
							{searchQuery ? (
								<Typography variant="body2" color="text.secondary">
									Try adjusting your search criteria
								</Typography>
							) : (
								showOptedOut && (
									<Typography variant="body2" color="text.secondary">
										Every agent is currently opted in for service.
									</Typography>
								)
							)}
						</Box>
					</Grid>
				)}
			</Grid>

			{/* Pagination */}
			{!loading && filteredAgents.length > 0 && (
				<Pagination
					totalPages={pageCount}
					currentPage={currentPage}
					onPageChange={setCurrentPage}
					itemsPerPage={pageSize}
					totalItems={filteredAgents.length}
					itemLabel="agents"
				/>
			)}

			<AgentSlidingPanel
				open={openPanel}
				onClose={() => setOpenPanel(false)}
				agent={selectedAgent}
				setAgents={setAgents}
				setSnackbar={setSnackbar}
				handleUpdateAgentProfile={handleUpdateAgentProfile}
				handleUpdateAgentAvatar={handleUpdateAgentAvatar}
				handleSendPasswordSetup={handleUpdateAgentPassword}
				updateProfileLoading={updateProfileLoading}
				updateAvatarLoading={updateAvatarLoading}
				passwordSetupLoading={passwordSetupLoading}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				sx={{ zIndex: 9999 }}
			>
				<Alert severity={snackbar.severity} sx={{ width: "100%", fontWeight: 500 }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};
export default AgentManagement;
